import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { BookOpen, ArrowRight, Calendar } from "lucide-react";
import { Link } from "react-router-dom";

const BlogSection = ({ blogPosts }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0 }
  };

  // Default blog posts if none exist
  const defaultPosts = [
    {
      id: "default-1",
      title: "Understanding the Role of an End of Life Doula",
      excerpt: "Learn how an end-of-life doula provides emotional, spiritual, and practical support during life's final transition, and why this care is invaluable for families.",
      image_url: "https://images.unsplash.com/photo-1743956242693-cc024361ae9c?w=600",
      created_at: new Date().toISOString()
    },
    {
      id: "default-2",
      title: "Preparing for a Peaceful Transition",
      excerpt: "Guidance on how families can prepare emotionally and practically for their loved one's end-of-life journey, including conversations to have and decisions to make.",
      image_url: "https://images.unsplash.com/photo-1723249796346-2549649b6e58?w=600",
      created_at: new Date().toISOString()
    },
    {
      id: "default-3",
      title: "The Importance of Legacy Work",
      excerpt: "Discover how legacy projects help preserve memories, provide closure, and create lasting gifts for families during the end-of-life process.",
      image_url: "https://images.unsplash.com/photo-1711702362278-754f10d6f7db?w=600",
      created_at: new Date().toISOString()
    }
  ];

  const displayPosts = blogPosts?.length > 0 ? blogPosts : defaultPosts;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <section 
      id="blog" 
      className="relative py-24 md:py-32 bg-[#E8E4D9]"
      ref={ref}
      data-testid="blog-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={fadeInUp}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="text-sm uppercase tracking-widest text-[#8F9E82] font-bold mb-4">
            Knowledge & Guidance
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-medium text-[#2D2A26] mb-6">
            Resources & Insights
          </h2>
          <p className="text-lg text-[#5C5852] max-w-2xl mx-auto">
            Helpful articles and resources to guide you through understanding end-of-life care.
          </p>
        </motion.div>

        {/* Blog Posts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {displayPosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial="hidden"
              animate={isInView ? "visible" : "hidden"}
              variants={fadeInUp}
              transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
              className="group bg-white rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(74,93,35,0.06)] hover:shadow-[0_12px_40px_rgba(74,93,35,0.12)] transition-all duration-300 hover:-translate-y-1"
              data-testid={`blog-card-${index}`}
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={post.image_url || "https://images.unsplash.com/photo-1743956242693-cc024361ae9c?w=600"}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 text-[#8F9E82] text-sm mb-3">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(post.created_at)}</span>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#2D2A26] mb-3 group-hover:text-[#4A5D23] transition-colors">
                  {post.title}
                </h3>
                <p className="text-[#5C5852] text-sm leading-relaxed mb-4 line-clamp-3">
                  {post.excerpt}
                </p>
                {!post.id.startsWith('default') && (
                  <Link
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center gap-2 text-[#4A5D23] font-medium text-sm group-hover:gap-3 transition-all"
                    data-testid={`read-more-${post.id}`}
                  >
                    <BookOpen className="w-4 h-4" />
                    Read More
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
